import { Button, Row, Select, Space, Typography, Tag, Alert } from "antd";
import React from "react";
import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./styles.css";
import "@styles/styles-PlayerOrdering.css";
import { MdDragIndicator } from "react-icons/md";
import { useEffect } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector, useDispatch } from "react-redux";
import { getTeamPlayers } from "../../store/actions/team.actions";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { 
  PermissionedButton, 
  PermissionedSelect 
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import PermissionOverlay from "../PermissionedComponents/PermissionOverlay";

const { Title } = Typography;

const SortablePlayerItem = ({ item, playerGroups, teamName }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${item.player_id}`,
  });
  const meta = [item.number != null && item.number !== "" && `#${item.number}`, item.position].filter(Boolean).join(" · ");

  return (
    <div
      className="player-order-card"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <span className="player-order-card-grip"><MdDragIndicator size={18} /></span>
      <div className="player-order-card-info">
        <div className="player-order-card-name">
          {formattedPlayerName(teamName, item.first_name + " " + item.last_name)}
        </div>
        {meta && <div className="player-order-card-meta">{meta}</div>}
      </div>
      <div className="player-order-card-groups">
        {item?.player_groups?.map((player_group) => {
          let pg_item = playerGroups.find((x) => x.player_group_id == player_group);
          return pg_item ? (
            <Tag
              key={player_group}
              color={tag_colors[Math.min(playerGroups.indexOf(pg_item), tag_colors.length - 1)]}
            >
              {pg_item.name}
            </Tag>
          ) : null;
        }) || <></>}
      </div>
    </div>
  );
};

const sortByOptions = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "number", label: "Number" },
  { value: "position", label: "Position" },
];
const sortOptions = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

const tag_colors = [
  "processing",
  "success",
  "error",
  "warning",
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
];

const trainingAPI = new TrainingAPI();

export const PlayerOrdering = () => {
  const dispatch = useDispatch();
  const {
    playerGroups,
    players,
    info: { display_name: teamName },
  } = useSelector(({ team }) => team);
  const [itemList, setItemList] = useState(players);
  const [sort, setSort] = useState("asc");
  const [sortBy, setSortBy] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false)

  useEffect(() => {
    setItemList(players);
  }, [players]);

  useEffect(() => {
    if (JSON.stringify(itemList) !== JSON.stringify(players) || itemList.every((e) => e?.custom_order === 0)) {
      setUnsavedChanges(true)
    } else {
      setUnsavedChanges(false)
    }
  }, [itemList])

  useEffect(() => {
    const sorted = [...itemList].sort((a, b) =>
      a[sortBy] < b[sortBy]
        ? sort === "asc"
          ? -1
          : 1
        : sort === "asc"
        ? 1
        : -1
    );
    setItemList(sorted);
  }, [sort, sortBy]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDrop = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = itemList.findIndex((item) => `${item.player_id}` === active.id);
    const newIndex = itemList.findIndex((item) => `${item.player_id}` === over.id);
    setItemList(arrayMove(itemList, oldIndex, newIndex));
  };

  const handleSortChange = (val) => {
    setSort(val);
  };

  const handleSortByChange = (val) => {
    setSortBy(val);
  };

  const handleSave = () => {
    trainingAPI
      .savePlayerOrdering(
        itemList.reduce(
          (cur, item, index) => ({ ...cur, [item.player_id]: index + 1 }),
          {}
        )
      )
      .then((data) => {
        message.success("Player order saved successfully");
        dispatch(getTeamPlayers());
      })
      .catch((err) => message.error("Error saving player order"));
  };

  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: 750, maxWidth: "100%", margin: "0px 0px 24px" }}>
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Select
              className="player-order-sort-by"
              options={sortByOptions}
              value={sortBy}
              showSearch
              optionFilterProp="value"
              onSelect={handleSortByChange}
              placeholder="Select Sort By"
            />
            <Select
              className="player-order-sort-dir"
              options={sortOptions}
              value={sort}
              showSearch
              optionFilterProp="value"
              onSelect={handleSortChange}
            />
            <PermissionedButton
              permissionCheck="EditPlayerOrdering"
              type="primary"
              onClick={handleSave}
              className={unsavedChanges && 'pulse'}
              disabled={unsavedChanges === false}
            >
              Save Order
            </PermissionedButton>
          {unsavedChanges && 
          <Alert
            message="You have unsaved changes!"
            type="info"
            showIcon
          />
        }
        </div>
        <PermissionOverlay
          permissionCheck="EditPlayerOrdering"
          permissionMessage="Player ordering is restricted"
          tooltipMessage="You don't have permission to reorder players"
          hideTooltip={false}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDrop}
          >
            <SortableContext
              items={itemList.map((item) => `${item.player_id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="player-order-list">
                {itemList.map((item) => (
                  <SortablePlayerItem
                    key={item.player_id}
                    item={item}
                    playerGroups={playerGroups}
                    teamName={teamName}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </PermissionOverlay>
      </div>
    </div>
  );
};
